/**
 * AttributeTemplatePicker - "select an existing attribute or create a new
 * one" control shown at the top of every attribute create/edit dialog
 * (Entity Editor, product/host/transaction templates, warranty classes,
 * claim schemas, workflow attributes).
 *
 * Selecting an existing shared attribute template prefills display_name,
 * code_name, field_type and configuration_data (options) on the caller's
 * form, and the caller locks field_type + the options editor because those
 * are owned by the shared attribute. Typing a new, unmatched name leaves
 * field_type/options fully editable, exactly like today.
 *
 * This component owns its own search state (results/loading/fetching) so it
 * can resolve the full template row for a selected option before handing a
 * clean, discriminated result to the caller via `onPick`.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'

import { Label } from '@/shared/components/ui/label'
import {
  ResourceSearchInput,
  type ResourceOption,
} from '@/shared/components/ui/resource-search-input'
import { useResourceLookup } from '@/shared/components/ui/resource-lookup-context'

export interface AttributeTemplateResult {
  uuid: string
  code_name: string
  display_name: string
  description?: string
  field_type: string
  hint?: string
  configuration_data?: Record<string, unknown>
  category?: string
  is_suggested?: boolean
}

export type AttributeTemplatePickResult =
  | { type: 'existing'; template: AttributeTemplateResult }
  | { type: 'create'; name: string }
  | { type: 'clear' }

interface AttributeTemplatePickerProps {
  onPick: (result: AttributeTemplatePickResult) => void
  className?: string
  /** Clear the selection immediately after a pick. Use when the picker is an
   *  "add" entry point (select attaches / create opens a modal) rather than a
   *  persistent form field, so it is ready for the next add. */
  resetAfterPick?: boolean
  /** Hide the descriptive label above the input (for compact "add" usage). */
  hideLabel?: boolean
  /**
   * Render as a "+ Add attribute" button that opens the select-or-create
   * dropdown, instead of a persistent combobox field. Use at list headers.
   */
  asAddButton?: boolean
  /** Label for the add button (defaults to "Add attribute"). */
  addButtonLabel?: string
  /**
   * Surface/domain this picker serves (e.g. "product", "claim"). Scopes the
   * shared bucket so only attributes used on this surface (plus the suggested
   * library) are shown. Omit to search the whole tenant bucket.
   */
  domain?: string
  'data-testid'?: string
}

export function AttributeTemplatePicker({
  onPick,
  className,
  resetAfterPick = false,
  hideLabel = false,
  asAddButton = false,
  addButtonLabel,
  domain,
  'data-testid': dataTestId,
}: AttributeTemplatePickerProps) {
  const { t } = useTranslation()
  const provider = useResourceLookup('attribute_template')

  const [value, setValue] = useState<{ uuid: string; name: string } | null>(null)
  const [results, setResults] = useState<ResourceOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)

  const runSearch = (term: string) => {
    if (!provider) {
      setResults([])
      return
    }
    setIsFetching(true)
    if (!hasLoaded) setIsLoading(true)
    provider
      .search(term, domain ? { domain } : undefined)
      .then((opts) => setResults(opts ?? []))
      .catch(() => setResults([]))
      .finally(() => {
        setIsFetching(false)
        setIsLoading(false)
        setHasLoaded(true)
      })
  }

  // Prime the list with an empty search so opening the picker shows the full
  // (paginated) bucket right away, mirroring ResourceSearchField's pattern.
  useEffect(() => {
    runSearch('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider])

  const handleChange = (next: { uuid: string; name: string } | null) => {
    setValue(resetAfterPick ? null : next)
    if (!next) {
      onPick({ type: 'clear' })
      return
    }
    if (!next.uuid) {
      onPick({ type: 'create', name: next.name })
      return
    }
    const match = results.find((r) => r.uuid === next.uuid)
    if (match?.data) {
      onPick({
        type: 'existing',
        template: match.data as unknown as AttributeTemplateResult,
      })
    } else {
      onPick({ type: 'create', name: next.name })
    }
  }

  const searchPlaceholder = t(
    'templates.attribute.pickerPlaceholder',
    'Search shared attributes or type a new name...',
  )
  const emptyMessage = t(
    'templates.attribute.pickerEmpty',
    'No shared attributes found',
  )

  return (
    <div className={className} data-testid={asAddButton ? undefined : dataTestId}>
      {!hideLabel && !asAddButton && (
        <Label className="clara-section-label mb-1.5 block">
          {t(
            'templates.attribute.pickerLabel',
            'Use an existing shared attribute or create a new one',
          )}
        </Label>
      )}
      <ResourceSearchInput
        value={value}
        onChange={handleChange}
        results={results}
        onSearch={runSearch}
        isLoading={isLoading}
        isFetching={isFetching}
        iconName="tag"
        searchPlaceholder={searchPlaceholder}
        filterPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        allowCreate
        triggerChildren={
          asAddButton ? (
            <>
              <Plus className="h-3 w-3" />
              {addButtonLabel ?? t('templates.addAttribute', 'Add attribute')}
            </>
          ) : undefined
        }
        triggerClassName={
          asAddButton
            ? 'flex h-auto cursor-pointer items-center gap-1.5 rounded-[4px] px-3 py-1.5 text-[11px] text-[var(--ink-3)] transition-all duration-150 hover:bg-[var(--bg-subtle)]'
            : undefined
        }
        triggerTestId={asAddButton ? dataTestId : undefined}
        popupClassName={asAddButton ? 'w-[340px]' : undefined}
      />
    </div>
  )
}
